import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import TrustBadges from "@/components/TrustBadges";
import { Heart, Star } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart, toggleWishlist, inWishlist } = useApp();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ author: "", rating: 5, comment: "" });
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState({ show: false, x: 0.5, y: 0.5 });
  const mainRef = useRef(null);

  useEffect(() => {
    api.get(`/products-v2/${id}`).then(({ data }) => {
      setProduct(data);
      // Choose first size that has stock; else first size
      const first =
        (data.sizes || []).find((s) => (data.size_stock?.[s] ?? 0) > 0) ||
        (data.sizes || [])[0] ||
        "";
      setSize(first);
      setActiveImg(0);
    });
    api.get(`/products/${id}/reviews`).then(({ data }) => setReviews(data));
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.author || !form.comment) return toast.error("Please add your name and comment");
    const { data } = await api.post("/reviews", { ...form, product_id: id });
    setReviews([data, ...reviews]);
    setForm({ author: "", rating: 5, comment: "" });
    toast.success("Thanks for your review.");
  };

  const images = useMemo(() => {
    if (!product) return [];
    return product.images && product.images.length ? product.images : product.image ? [product.image] : [];
  }, [product]);

  const currentStock = product && size ? Number(product.size_stock?.[size] ?? 0) : 0;
  const outOfStock = product && Number(product.stock ?? 0) === 0;
  const sizeAvailable = currentStock > 0;

  const onMouseMove = (e) => {
    const el = mainRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setZoom({ show: true, x, y });
  };

  const hasDiscount =
    product?.discount_price && product.discount_price > 0 && product.discount_price < product.price;

  if (!product) return <div className="min-h-[60vh] grid place-items-center label-xs">Loading…</div>;

  const priceToUse = hasDiscount ? product.discount_price : product.price;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10" data-testid="product-detail-page">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
        {/* Gallery */}
        <div className="lg:sticky lg:top-28 h-fit">
          <div
            ref={mainRef}
            onMouseMove={onMouseMove}
            onMouseLeave={() => setZoom((z) => ({ ...z, show: false }))}
            className="relative w-full aspect-[4/5] overflow-hidden bg-muted cursor-zoom-in"
            data-testid="product-detail-gallery"
          >
            <img
              src={images[activeImg]}
              alt={product.name}
              className="w-full h-full object-cover select-none"
              data-testid="product-detail-image"
              draggable={false}
              style={
                zoom.show
                  ? {
                      transform: `scale(2)`,
                      transformOrigin: `${zoom.x * 100}% ${zoom.y * 100}%`,
                      transition: "transform 200ms ease-out",
                    }
                  : { transition: "transform 300ms ease-out" }
              }
            />
            {outOfStock && (
              <div className="absolute inset-0 grid place-items-center bg-background/60">
                <span className="border border-foreground bg-background px-6 py-3 label-xs tracking-[0.3em]">
                  SOLD OUT
                </span>
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-2" data-testid="product-thumbnails">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`relative aspect-square border overflow-hidden transition-opacity ${
                    activeImg === i ? "border-foreground" : "border-foreground/20 opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`thumb-${i}`}
                  style={{ transitionProperty: "opacity, border-color" }}
                >
                  <img src={src} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div>
          <p className="label-xs opacity-60">{product.category}</p>
          <h1 className="mt-3 text-4xl lg:text-5xl font-serif tracking-tight" data-testid="product-name">
            {product.name}
          </h1>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-foreground" : "opacity-30"}`} />
              ))}
            </div>
            <span className="text-sm opacity-70">
              {product.rating} · {product.reviews_count} reviews
            </span>
          </div>

          <div className="mt-6 flex items-baseline gap-3" data-testid="product-detail-price">
            <span className="text-3xl font-body">{money(priceToUse)}</span>
            {hasDiscount && <span className="text-lg opacity-40 line-through">{money(product.price)}</span>}
            {hasDiscount && (
              <span className="ml-2 px-2 py-1 bg-foreground text-background label-xs">
                {Math.round(((product.price - product.discount_price) / product.price) * 100)}% OFF
              </span>
            )}
          </div>
          <p className="text-xs opacity-60">Inclusive of all taxes</p>

          {product.description && <p className="mt-8 opacity-80 leading-relaxed">{product.description}</p>}

          {/* Size selector with per-size stock awareness */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <p className="label-xs">
                Size {size ? <span className="opacity-60">· {size}</span> : ""}
              </p>
              <button className="label-xs opacity-70 link-underline">Size Guide</button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(product.sizes || []).map((s) => {
                const stk = Number(product.size_stock?.[s] ?? 0);
                const disabled = stk <= 0;
                return (
                  <button
                    key={s}
                    onClick={() => !disabled && setSize(s)}
                    disabled={disabled}
                    data-testid={`size-btn-${s}`}
                    aria-label={disabled ? `${s} out of stock` : `Choose size ${s}`}
                    title={disabled ? "Out of stock" : `${stk} left`}
                    className={`relative w-14 h-14 border transition-colors label-xs ${
                      size === s
                        ? "bg-foreground text-background border-foreground"
                        : disabled
                        ? "border-foreground/10 opacity-40 cursor-not-allowed line-through"
                        : "border-foreground/20 hover:border-foreground"
                    }`}
                    style={{ transitionProperty: "background-color, color, border-color" }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            {size && sizeAvailable && currentStock <= 5 && (
              <p className="mt-3 text-xs" data-testid="low-stock-warning">
                Only {currentStock} left in size {size}.
              </p>
            )}
            {size && !sizeAvailable && !outOfStock && (
              <p className="mt-3 text-xs opacity-70">Size {size} is currently unavailable.</p>
            )}
          </div>

          <div className="mt-8 flex gap-3">
            <div className="flex border border-foreground/20">
              <button
                className="w-10 h-14"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-decrement"
              >
                −
              </button>
              <span className="w-14 h-14 grid place-items-center border-x border-foreground/20" data-testid="qty-value">
                {qty}
              </span>
              <button
                className="w-10 h-14"
                onClick={() => setQty((q) => Math.min(q + 1, Math.max(1, currentStock || q + 1)))}
                data-testid="qty-increment"
              >
                +
              </button>
            </div>
            <Button
              variant="outline"
              className="flex-1 rounded-none h-14 label-xs border-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() =>
                addToCart({ ...product, price: priceToUse }, size, Math.min(qty, currentStock || qty))
              }
              disabled={outOfStock || !sizeAvailable || !size}
              data-testid="add-to-cart-btn"
            >
              {outOfStock ? "Sold Out" : !sizeAvailable ? "Size Unavailable" : "Add to Cart"}
            </Button>
            <button
              onClick={() => toggleWishlist(product)}
              className={`w-14 h-14 border grid place-items-center transition-colors ${
                inWishlist(product.id)
                  ? "bg-foreground text-background border-foreground"
                  : "border-foreground/20 hover:border-foreground"
              }`}
              style={{ transitionProperty: "background-color, color, border-color" }}
              data-testid="wishlist-toggle-btn"
              aria-label="Toggle wishlist"
            >
              <Heart className={`h-4 w-4 ${inWishlist(product.id) ? "fill-background" : ""}`} />
            </button>
          </div>

          {!outOfStock && sizeAvailable && (
            <p className="mt-3 text-xs opacity-60 text-center">
              Secure checkout · COD or Online · Ships across India
            </p>
          )}

          {product.sku && <p className="mt-4 text-xs opacity-50">SKU: {product.sku}</p>}

          <div className="mt-8">
            <TrustBadges variant="compact" />
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-24" data-testid="reviews-section">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="text-3xl font-serif">Customer Reviews</h2>
          <span className="label-xs opacity-60">{reviews.length} REVIEWS</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            {reviews.map((r) => (
              <div key={r.id} className="border-b pb-6" data-testid={`review-${r.id}`}>
                <div className="flex justify-between items-center">
                  <div className="font-serif text-lg">{r.author}</div>
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-foreground" : "opacity-30"}`} />
                    ))}
                  </div>
                </div>
                <p className="mt-2 opacity-80 leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
          <form onSubmit={submitReview} className="border p-8 h-fit space-y-4" data-testid="review-form">
            <p className="label-xs">Write a Review</p>
            <Input
              placeholder="Your name"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              className="rounded-none"
              data-testid="review-author"
            />
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setForm({ ...form, rating: n })}
                  data-testid={`review-rating-${n}`}
                >
                  <Star className={`h-6 w-6 ${n <= form.rating ? "fill-foreground" : "opacity-30"}`} />
                </button>
              ))}
            </div>
            <Textarea
              placeholder="Share your experience…"
              rows={4}
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              className="rounded-none"
              data-testid="review-comment"
            />
            <Button type="submit" className="rounded-none label-xs w-full h-12" data-testid="submit-review-btn">
              Submit Review
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
